import React from "react";
import { Link } from "react-router-dom";
import { useSharedHomepageData } from "../../hooks/queries/useSharedHomepageData";
import { homepageApiService } from "../../services/homepageApiService";
import { LoadingSkeleton } from "../ui/LoadingStates";
import { ErrorMessage } from "../ui/ErrorStates";
import OptimizedContentImage from "../common/OptimizedContentImage";

interface LatestBlogWidgetContentProps {
  count?: number;
  showFeaturedOnly?: boolean;
}

/**
 * LatestBlogWidgetContent - Content-only version without container styling
 * Used by HomePage variations to avoid styling conflicts
 */
const LatestBlogWidgetContent: React.FC<LatestBlogWidgetContentProps> = ({
  count = 3,
  showFeaturedOnly = false,
}) => {
  const {
    data: homepageData,
    isLoading,
    error,
    refetch,
  } = useSharedHomepageData();

  if (isLoading) {
    return (
      <>
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <div>
            <LoadingSkeleton height="h-8" width="w-48" className="mb-2" />
            <LoadingSkeleton height="h-4" width="w-32" />
          </div>
          <LoadingSkeleton height="h-4" width="w-20" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: count }).map((_, index) => (
            <div key={index} className="bg-gray-800 rounded-lg overflow-hidden">
              <LoadingSkeleton height="h-48" width="w-full" />
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <LoadingSkeleton height="h-5" width="w-16" />
                  <LoadingSkeleton height="h-5" width="w-16" />
                </div>
                <LoadingSkeleton height="h-5" width="w-full" />
                <LoadingSkeleton height="h-4" width="w-full" />
                <LoadingSkeleton height="h-4" width="w-3/4" />
                <div className="flex items-center justify-between">
                  <LoadingSkeleton height="h-3" width="w-20" />
                  <LoadingSkeleton height="h-3" width="w-16" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        type="network"
        title="Failed to Load Blog Posts"
        message="Unable to load latest blog posts. Please try again."
        onRetry={() => refetch()}
        showRetry={true}
        className="bg-gray-800/50 rounded-lg p-6"
      />
    );
  }

  const posts = homepageData?.blog_posts || [];

  // Filter posts based on widget parameters
  let filteredPosts = posts;
  if (showFeaturedOnly) {
    filteredPosts = posts.filter((post) => post.featured);
  }
  // Limit to requested count
  filteredPosts = filteredPosts.slice(0, count);

  if (filteredPosts.length === 0) {
    return null;
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">
            Latest Blog Posts
          </h2>
        </div>
        <Link
          to="/blog"
          className="text-red-500 hover:text-red-400 transition-colors text-sm font-medium"
        >
          View All →
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPosts.map((post) => (
          <BlogPostCard key={post.id} post={post} />
        ))}
      </div>
    </>
  );
};

interface BlogPostCardProps {
  post: {
    id: string;
    title: string;
    slug: string;
    excerpt: string;
    featured_image: string;
    category: string;
    tags?: string[];
    featured: boolean;
    publish_date: string;
    created_at: string;
    updated_at: string;
  };
}

const BlogPostCard: React.FC<BlogPostCardProps> = ({ post }) => {
  const imageUrl = post.featured_image
    ? homepageApiService.getImageUrl(post.featured_image, "blog")
    : null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Link to={`/blog/${post.slug}`} className="group">
      <article className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors">
        {imageUrl && (
          <div className="aspect-video overflow-hidden">
            <OptimizedContentImage
              src={imageUrl}
              alt={post.title}
              className="w-full h-full"
              imageClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        )}

        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs bg-red-800 text-white px-2 py-1 rounded">
              {post.category}
            </span>
            {post.featured && (
              <span className="text-xs bg-yellow-600 text-white px-2 py-1 rounded">
                Featured
              </span>
            )}
          </div>

          <h3 className="text-white font-semibold mb-2 group-hover:text-red-400 transition-colors line-clamp-2">
            {post.title}
          </h3>

          <p className="text-gray-300 text-sm mb-3 line-clamp-2">
            {post.excerpt}
          </p>

          <div className="flex items-center justify-between text-xs text-slate-200">
            <time dateTime={post.publish_date}>
              {formatDate(post.publish_date)}
            </time>

            {post.tags && post.tags.length > 0 && (
              <div className="flex gap-1">
                {post.tags.slice(0, 2).map((tag) => (
                  <span key={tag} className="text-slate-300">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
};

export default LatestBlogWidgetContent;
